import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { type Consequence } from "@src/appV2/redesign/ClipboardScore/components/Activity/ConsequencePill";
import { ConsequenceText } from "@src/appV2/redesign/ClipboardScore/components/Activity/ConsequenceText";
import { MultiplierPill } from "@src/appV2/redesign/ClipboardScore/components/Activity/MultiplierPill";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Card, CbhIcon, Pill, Text, TextSkeleton } from "@src/appV2/redesign/system/ui-components";

import { isRebrandingEnabled } from "../../../system/ui-theme";

export interface CancelShiftContentProps {
  isLoading: boolean;
  isRestricted: boolean;
  isDynamicPenaltiesEnabled: boolean;
  maximumShiftsToCheckForCancellationPenalty?: number;
  currentClipboardScore?: number;
  newClipboardScore?: number;
  isScoreFrozen?: boolean;
  isInviteRequired: boolean;
  multiplier?: number;
  futureShiftsCount: number;
  nextRestrictionDuration?: number;
  policyToBeApplied: {
    label: string;
    consequence: Consequence | undefined;
  };
  onPenaltyExplanationClick?: () => void;
  scoreChange?: number | undefined;
  isOrientationBlockShift: boolean;
}

export function CancelShiftContent(props: CancelShiftContentProps) {
  const {
    isLoading,
    isRestricted,
    isDynamicPenaltiesEnabled,
    maximumShiftsToCheckForCancellationPenalty,
    currentClipboardScore = 0,
    newClipboardScore = 0,
    isScoreFrozen = false,
    isInviteRequired,
    multiplier,
    futureShiftsCount,
    nextRestrictionDuration,
    policyToBeApplied,
    onPenaltyExplanationClick,
    scoreChange,
    isOrientationBlockShift,
  } = props;

  const hasPointsToDeduct = isDefined(scoreChange) && scoreChange < 0;

  if (isLoading) {
    return (
      <Stack justifyContent="center" alignItems="center">
        <TextSkeleton variant="h2" width="10ch" />
        <TextSkeleton variant="h4" width="20ch" />
        <TextSkeleton variant="h4" width="20ch" />
      </Stack>
    );
  }

  const willBeRestricted = currentClipboardScore > 0 && newClipboardScore <= 0;

  function isMultiplierActive() {
    return isDefined(multiplier) && multiplier > 1;
  }

  function getDescription() {
    if (!hasPointsToDeduct) {
      return "There is no penalty for cancelling this shift.";
    }

    if (isRestricted || isInviteRequired) {
      return "Showing up helps you remain in good standing with the workplace who has invited you!";
    }

    if (isDynamicPenaltiesEnabled && isDefined(maximumShiftsToCheckForCancellationPenalty)) {
      if (isMultiplierActive()) {
        return `You had a same-day cancellation in your last ${maximumShiftsToCheckForCancellationPenalty} shifts, so this one costs more.`;
      }

      return "Your past reliability has earned you a lower penalty for this cancellation.";
    }

    return "";
  }

  function getTitle() {
    if (isInviteRequired) {
      return "Your account is Invite-Required";
    }

    if (isRestricted) {
      return "Your account is Restricted";
    }

    return "Your Clipboard Score";
  }

  function getHeading(isOrientationBlockShift: boolean) {
    if (isOrientationBlockShift) {
      return "Cancel your entire orientation block?";
    }

    return "Are you sure?";
  }

  const showClipboardScore = !(isRestricted || isInviteRequired || isScoreFrozen);
  const showCard = isRestricted || isInviteRequired || !isScoreFrozen;

  return (
    <DialogContent
      sx={{
        px: 5,
        py: 6,
        pt: 10,
        background: isRebrandingEnabled()
          ? undefined
          : // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
            "linear-gradient(180deg, rgba(250, 248, 247, 0.00) 0%, #FFF 72.7%), linear-gradient(117deg, #F6F3F1 18.83%, rgba(249, 225, 225, 0.84) 96.06%)",
      }}
    >
      <Stack spacing={8} justifyContent="center" alignItems="center">
        {hasPointsToDeduct && showCard && (
          <Card
            sx={{
              padding: 6,
              width: "85%",
              background: "rgba(255, 253, 253, 0.80)",
              boxShadow: "0 3.6px 44.8px 0 rgba(0, 0, 0, 0.07)",
              backdropFilter: "blur(23px)",
            }}
          >
            <Stack spacing={7} justifyContent="center" alignItems="center">
              <Stack spacing={5} justifyContent="center" alignItems="center">
                <CbhIcon type="clipboard-score-unhealthy-colored" size="xLarge" />
                <Text variant="h6" style={{ fontWeight: 500 }}>
                  {getTitle()}
                </Text>
                {showClipboardScore && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={10}
                  >
                    <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                      {policyToBeApplied.label}
                    </Text>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {isDefined(multiplier) && <MultiplierPill multiplier={multiplier} />}
                      {isDefined(policyToBeApplied.consequence) && (
                        <ConsequenceText consequence={policyToBeApplied.consequence} />
                      )}
                    </Stack>
                  </Stack>
                )}
              </Stack>
              {showClipboardScore && (
                <Pill
                  label={
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CbhIcon
                        type="diamond"
                        size="medium"
                        sx={{ pr: 2, color: (theme) => theme.palette.error.main }}
                      />
                      <Text inline color={(theme) => theme.palette.text.tertiary}>
                        {currentClipboardScore}
                      </Text>
                      <CbhIcon
                        type="arrow-right"
                        size="medium"
                        sx={{ pr: 2, color: (theme) => theme.palette.text.tertiary }}
                      />
                      <Text inline color={(theme) => theme.palette.error.main}>
                        {newClipboardScore}
                      </Text>
                    </Stack>
                  }
                  variant="outlined"
                />
              )}
              {willBeRestricted && (
                <Text
                  variant="caption"
                  color={(theme) => theme.palette.error.main}
                  textAlign="center"
                >
                  Your account will be Restricted. You&apos;ll be{" "}
                  {futureShiftsCount > 0
                    ? `removed from your ${futureShiftsCount} future shifts, and `
                    : ""}
                  unable to book new shifts for {nextRestrictionDuration} days.
                </Text>
              )}
            </Stack>
          </Card>
        )}
        <Stack
          spacing={6}
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          paddingX={8}
        >
          <Text variant="h2">{getHeading(isOrientationBlockShift)}</Text>
          <Text color={(theme) => theme.palette.text.secondary}>{getDescription()}</Text>
          {isOrientationBlockShift && (
            <Text color={(theme) => theme.palette.text.secondary}>
              Cancelling this shift will remove the entire orientation block. You&apos;ll need to
              restart the block to book at this workplace.
            </Text>
          )}
        </Stack>
        {isDynamicPenaltiesEnabled &&
          isMultiplierActive() &&
          isDefined(onPenaltyExplanationClick) && (
            <TextButton onClick={onPenaltyExplanationClick}>Why is this penalty higher?</TextButton>
          )}
      </Stack>
    </DialogContent>
  );
}
